import React, { useCallback, useEffect, useRef, useState } from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import * as d3 from 'd3';

import d3Heatmap from './d3.heatmap';
// eslint-disable-next-line css-modules/no-unused-class
import styles from './Heatmap.css'; // 'unused' styles are actually used in `d3.heatmap.js`
import { EmptyObject } from '../shared/sharedTypes';
import Tooltip from '../ui/Tooltip';

type SVGLayoutType = {
  addColumnCountAxis: boolean;
  cellBorderSize: number;
  margins: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  rowBuffer: number;
  tooltipOffset: {
    x: number;
    y: number;
  };
  xAxisTextOffset: {
    x: number;
    y: number;
  };
  yAxisWidth: number;
};

type CellType = {
  value: string;
  row: number;
  colum: number;
  blank?: boolean;
};

type ColumnHeaderType = {
  name: string; // required, minimum
  onClick?: () => any;
  link?: boolean;
  variantIdx?: number;
  variantScore?: number;
};

type RowHeaderType = {
  name: string;
  onClick?: () => any;
  link?: boolean;
  feature?: string;
};

type HeatmapPropTypes = {
  color: () => any;
  columnValues: ColumnHeaderType[];
  heatmapValues: CellType[];
  rowValues: RowHeaderType[];
  cellHeight: number;
  cellWidth: number;
  columnHeaderRotation: number;
  heatmapInfo:
    | {
        yHeaderName: string; // cell [0,0]
        heatmapId: string; // Set reference to SVG
        chr: string; // all these are for the whole heatmap
        targetXPos: string;
        targetXIndex?: number; // can be blank, mostly for style
      }
    | EmptyObject;
  lockXAxis: boolean;
  maxBodyWidth: number;
  onDrag: () => any;
  scrollIntoView: boolean;
  style: EmptyObject | ((arg0: any) => any);
  svgLayout: SVGLayoutType;
  tooltips: (cell: CellType) => React.FunctionComponent;
  id: string;
};

// Type for hovered cell state
type HoveredCellState = {
  cell: CellType;
  rect: DOMRect;
} | null;

// Stable default objects - must be outside component to prevent re-renders
const DEFAULT_HEATMAP_INFO = {};
const DEFAULT_STYLE = {};
const DEFAULT_SVG_LAYOUT: SVGLayoutType = {
  addColumnCountAxis: false,
  cellBorderSize: 1,
  margins: { top: 100, right: 50, bottom: 20, left: 200 },
  rowBuffer: 25,
  tooltipOffset: { x: 50, y: 20 },
  xAxisTextOffset: { x: 1, y: 0.5 },
  yAxisWidth: 185,
};

const Heatmap = ({
  cellHeight = 15,
  cellWidth = 15,
  columnHeaderRotation = -65,
  heatmapInfo = DEFAULT_HEATMAP_INFO,
  lockXAxis = false,
  heatmapValues,
  rowValues,
  columnValues,
  color,
  tooltips,
  scrollIntoView = true,
  style = DEFAULT_STYLE,
  svgLayout = DEFAULT_SVG_LAYOUT,
  maxBodyWidth = 800,
  onDrag,
  id = 'heatmap-id',
}: HeatmapPropTypes) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [yAxisWidth, setYAxisWidth] = useState(svgLayout.yAxisWidth);
  const [latestCellHeight, setLatestCellHeight] = useState(cellHeight);
  const [hoveredCell, setHoveredCell] = useState<HoveredCellState>(null);

  // Callbacks for D3 to communicate hover state - immediate updates
  const onCellHover = useCallback((cell: CellType, element: SVGRectElement) => {
    setHoveredCell({ cell, rect: element.getBoundingClientRect() });
  }, []);

  const onCellLeave = useCallback(() => {
    setHoveredCell(null);
  }, []);
  const updateHeatmap = useCallback(() => {
    containerRef.current?.replaceChildren();

    const heatmap = d3Heatmap();

    heatmap
      .setColumnHeaderRotation(columnHeaderRotation)
      .setLockXAxis(lockXAxis)
      .setLayout(svgLayout)
      .setCellHeight(cellHeight)
      .setCellWidth(cellWidth)
      .data(heatmapValues)
      .rows(rowValues)
      .columns(columnValues)
      .colors(color)
      .info(heatmapInfo)
      .maxBodyWidth(maxBodyWidth)
      .id(id)
      .drag(onDrag)
      .onCellHover(onCellHover)
      .onCellLeave(onCellLeave);

    if (tooltips) heatmap.tooltips(tooltips);
    d3.select(containerRef.current).call(heatmap);
    if (scrollIntoView) heatmap.scrollIntoView();

    setYAxisWidth(heatmap.getRowHeaderWidth());
    setLatestCellHeight(heatmap.getCellHeight());
  }, [
    columnHeaderRotation,
    lockXAxis,
    svgLayout,
    cellHeight,
    cellWidth,
    heatmapValues,
    rowValues,
    columnValues,
    color,
    heatmapInfo,
    maxBodyWidth,
    id,
    onDrag,
    onCellHover,
    onCellLeave,
    tooltips,
    scrollIntoView,
  ]);

  useEffect(() => {
    updateHeatmap();
  }, [updateHeatmap]);

  // Clear hover state when data changes - indices become invalid
  useEffect(() => {
    setHoveredCell(null);
  }, [heatmapValues, columnValues, rowValues]);

  const heatmapStyles =
    typeof style === 'function' ? style({ rowHeaderWidth: yAxisWidth }) : style;

  // Generate tooltip content when cell is hovered
  const tooltipContent =
    hoveredCell && tooltips ? tooltips(hoveredCell.cell) : null;

  return (
    <>
      <div
        style={{
          // prevent line-height from affecting height
          minHeight: `${latestCellHeight * rowValues.length}px`,
          ...heatmapStyles,
        }}
        ref={containerRef}
      />
      {hoveredCell && tooltipContent && (
        <Tooltip content={tooltipContent} show placement="right" maxWidth={300}>
          <span
            style={{
              position: 'fixed',
              display: 'inline-block',
              left: hoveredCell.rect.left,
              top: hoveredCell.rect.top,
              width: hoveredCell.rect.width,
              height: hoveredCell.rect.height,
              pointerEvents: 'none',
            }}
          />
        </Tooltip>
      )}
    </>
  );
};

export default React.memo(withStyles(styles)(Heatmap));
